/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { ExternalLink } from "@app/vue/components/ExternalLink";

export interface ReservationContactInfoProps {
  /** Contact details typed on the reservation / reservation request */
  partyEmail?: string | null;
  partyPhone?: string | null;
  /** Contact details on the linked customer file */
  customerEmail?: string | null;
  customerPhone?: string | null;
}

const normalizeEmail = (value?: string | null) =>
  value ? value.trim().toLowerCase() : "";

const normalizePhone = (value?: string | null) =>
  value ? value.replace(/\D/g, "").replace(/^0+/, "") : "";

const emailsDiffer = (party?: string | null, customer?: string | null) => {
  const a = normalizeEmail(party);
  const b = normalizeEmail(customer);
  return a !== "" && b !== "" && a !== b;
};

/**
 * The same number is often typed in national and international form
 * (0787641099 vs +41787641099), so after dropping the leading zeros a number
 * that ends with the other counts as the same.
 */
export const phonesDiffer = (
  party?: string | null,
  customer?: string | null,
) => {
  const a = normalizePhone(party);
  const b = normalizePhone(customer);
  if (a === "" || b === "") return false;
  return !(a.endsWith(b) || b.endsWith(a));
};

const ContactRow = defineComponent<{
  email?: string | null;
  phone?: string | null;
  label?: string;
  showEmpty: boolean;
}>({
  name: "ReservationContactRow",
  props: ["email", "phone", "label", "showEmpty"],
  setup(props) {
    const { t } = useLocalization();

    return () => (
      <div class="flex items-center justify-between gap-3 text-xs">
        <div class="flex items-center gap-2 min-w-0">
          {props.label && (
            <span class="text-[10px] font-semibold uppercase tracking-wider text-base-content/45 flex-shrink-0">
              {props.label}
            </span>
          )}
          {props.email ? (
            <ExternalLink
              href={`mailto:${props.email}`}
              class="flex items-center gap-1.5 min-w-0 text-primary/80 hover:text-primary transition-colors"
            >
              <span class="material-symbols-outlined text-sm">mail</span>
              <span class="truncate">{props.email}</span>
            </ExternalLink>
          ) : props.showEmpty ? (
            <span class="text-base-content/30">
              {t("reservations.general.no_email")}
            </span>
          ) : null}
        </div>
        {props.phone ? (
          <ExternalLink
            href={`tel:${props.phone}`}
            class="flex items-center gap-1.5 flex-shrink-0 text-primary/80 hover:text-primary transition-colors"
          >
            <span class="material-symbols-outlined text-sm">call</span>
            <span>{props.phone}</span>
          </ExternalLink>
        ) : props.showEmpty ? (
          <span class="flex items-center gap-1.5 flex-shrink-0 text-base-content/30">
            <span class="material-symbols-outlined text-sm">call</span>
            {t("reservations.general.no_phone")}
          </span>
        ) : null}
      </div>
    );
  },
});

/**
 * Email + phone line for reservation sheets. When the reservation's contact
 * details differ from the customer file, both are shown, each labelled.
 */
export const ReservationContactInfo =
  defineComponent<ReservationContactInfoProps>({
    name: "ReservationContactInfo",
    props: ["partyEmail", "partyPhone", "customerEmail", "customerPhone"],
    setup(props) {
      const { t } = useLocalization();

      return () => {
        const email = props.partyEmail || props.customerEmail;
        const phone = props.partyPhone || props.customerPhone;
        const emailDiffers = emailsDiffer(
          props.partyEmail,
          props.customerEmail,
        );
        const phoneDiffers = phonesDiffer(
          props.partyPhone,
          props.customerPhone,
        );

        if (!emailDiffers && !phoneDiffers) {
          return <ContactRow email={email} phone={phone} showEmpty={true} />;
        }

        return (
          <div class="space-y-1.5">
            <ContactRow
              label={t("reservations.details.contact_on_reservation")}
              email={email}
              phone={phone}
              showEmpty={true}
            />
            <ContactRow
              label={t("reservations.details.contact_on_customer_file")}
              email={emailDiffers ? props.customerEmail : null}
              phone={phoneDiffers ? props.customerPhone : null}
              showEmpty={false}
            />
          </div>
        );
      };
    },
  });
